import { Watch, Wifi, WifiOff, Battery, RefreshCw, Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";

interface Device {
  id: string;
  device_type: string;
  device_icon: string;
  is_connected: boolean;
  last_sync_at: string;
  battery_level: number | null;
  signal_strength: string | null;
}

interface DeviceConnectionCardProps {
  devices: Device[];
  hasData: boolean;
  onSync: () => void;
  onSeedData: () => void;
  isSyncing: boolean;
  isSeeding: boolean;
}

export function DeviceConnectionCard({
  devices,
  hasData,
  onSync,
  onSeedData,
  isSyncing,
  isSeeding,
}: DeviceConnectionCardProps) {
  return (
    <Card className="border-border/50 mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Watch className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Connected Devices</CardTitle>
              <CardDescription>
                {devices.length > 0
                  ? `${devices.length} device${devices.length > 1 ? "s" : ""} connected`
                  : "No devices connected"}
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            {!hasData && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSeedData}
                disabled={isSeeding}
              >
                {isSeeding ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Load Sample Data
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={onSync}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Now
            </Button>
          </div>
        </div>
      </CardHeader>
      {devices.length > 0 && (
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {devices.map((device) => (
              <div
                key={device.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
              >
                <span className="text-2xl">{device.device_icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {device.device_type}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {device.is_connected ? (
                      <>
                        <Wifi className="h-3 w-3 text-success" />
                        <span className="text-success">Connected</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="h-3 w-3 text-muted-foreground" />
                        <span>Disconnected</span>
                      </>
                    )}
                    <span>•</span>
                    <span>
                      {formatDistanceToNow(new Date(device.last_sync_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
                {device.battery_level && (
                  <Badge
                    variant="outline"
                    className={
                      device.battery_level > 50
                        ? "text-success border-success/30"
                        : device.battery_level > 20
                        ? "text-warning border-warning/30"
                        : "text-destructive border-destructive/30"
                    }
                  >
                    <Battery className="h-3 w-3 mr-1" />
                    {device.battery_level}%
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
